import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

import cn from 'clsx'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { getTimeDateToScheduleEmail } from '@disco/utils/emailCampaigns'
import type { TimeOption } from '@disco/utils/time-utils'
import {
  checkDateIsInPast,
  checkTimeIsInPast,
  formatTimeForDisplay,
  getTimeOfDate,
  guessCurrentTimezone,
  setTimeOfDate,
} from '@disco/utils/time-utils'

import { EmailCampaignSchedule, ScheduledValue } from 'types/campaign'

import { Box, Flex as RadixFlex, Text as DsText } from 'components/ds'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import DatePicker from 'components/ui/date-picker'
import DiscoModal from 'components/ui/disco-modal'
import Flex from 'components/ui/flex'
import Heading from 'components/ui/heading'
import InlineError from 'components/ui/inline-error'
import Label from 'components/ui/label'
import Stack from 'components/ui/stack'
import Text from 'components/ui/text'
import TimePicker from 'components/ui/time-picker'
import TimeZonePicker from 'components/ui/time-zone-picker'

import styles from './index.modules.css'

type ScheduleData = Pick<EmailCampaignSchedule, 'date' | 'time' | 'timeZone'>

const MAX_YEARS_AHEAD = 2

/**
 * Formats the selected date as an English string. The date is parsed back
 * with `new Date`, which only reads English month names, so it must not
 * follow the dayjs UI locale (STELLA-48).
 */
const toParseableDate = (date: dayjs.Dayjs): string =>
  date.locale('en').format('MMMM D, YYYY')

type Props = {
  setScheduleData: (data: {
    date: string
    time: ScheduledValue
    timeZone: ScheduledValue
  }) => void
  storedCampaignSchedule?: ScheduleData
  onClose: () => void
  caption: string
  description: string
  primaryButtonCaption: string
  errorExplanation?: string
  showInlineErrors?: boolean
  showCloseButton?: boolean
  preserveUtcOnTimezoneChange?: boolean
  initialUtcDatetime?: string
}
export const EmailSchedulerModal = ({
  setScheduleData,
  storedCampaignSchedule,
  onClose,
  caption,
  description,
  primaryButtonCaption,
  errorExplanation,
  showInlineErrors = true,
  showCloseButton = true,
  preserveUtcOnTimezoneChange = false,
  initialUtcDatetime,
}: Props): React.ReactNode | React.ReactElement<'div'> => {
  const prevSelectedTime = useRef<Date | undefined>(undefined)
  const initialDate = toParseableDate(dayjs(storedCampaignSchedule?.date.value))
  const initialTimeZone = storedCampaignSchedule
    ? storedCampaignSchedule.timeZone
    : guessCurrentTimezone()
  const initialTime = storedCampaignSchedule
    ? storedCampaignSchedule.time
    : null
  const [selectedDate, setSelectedDate] = useState<string | null | undefined>(
    initialDate,
  )
  const [selectedTimeZone, setSelectedTimeZone] =
    useState<ScheduledValue | null>(initialTimeZone)
  const [selectedTime, setSelectedTime] = useState<ScheduledValue | null>(
    initialTime,
  )
  const [errorMessage, setErrorMessage] = useState<{
    dateError: string | null
    timeError: string | null
  }>()
  const [minDate, setMinDate] = useState<Date>(new Date())
  const [initialStartTime, setInitialStartTime] = useState<
    TimeOption | undefined
  >()

  const formattedStartTime = () => {
    const time = formatTimeForDisplay('08:00', initialDate)
    return time
  }

  const initialFormattedStartTime = formattedStartTime()

  const handleDateChange = date => {
    setSelectedDate(date)

    if (!selectedTime) {
      setInitialStartTime(undefined)
    }
  }

  const handleTimeChange = time => {
    setSelectedTime(time)
  }

  const handleTimeZoneChange = newTimeZone => {
    if (
      preserveUtcOnTimezoneChange &&
      selectedDate &&
      selectedTime &&
      selectedTimeZone?.value
    ) {
      const currentUtc = getTimeDateToScheduleEmail(
        new Date(selectedDate),
        selectedTime.value,
        selectedTimeZone.value,
      )
      const inNewTz = dayjs.utc(currentUtc).tz(newTimeZone.value)
      setSelectedDate(toParseableDate(inNewTz))
      setSelectedTime({
        value: inNewTz.format('HH:mm'),
        label: inNewTz.format('h:mm A'),
      })
    }
    setSelectedTimeZone(newTimeZone)
  }

  const selectedDateTime = setTimeOfDate(
    new Date(selectedDate || initialDate),
    selectedTime?.value || '',
  )
  // @ts-expect-error
  const zonedTimeNow = getTimeOfDate(dayjs(), selectedTimeZone?.value)
  const formatZonedTimeNow = dayjs(zonedTimeNow).format('YYYY-MM-DDTHH:mm:ss')
  const isDateInPast = checkDateIsInPast(
    selectedDate || initialDate,
    selectedTimeZone?.value || '',
  )
  const isTimeInPast = checkTimeIsInPast(
    // @ts-expect-error
    dayjs(prevSelectedTime?.current),
    formatZonedTimeNow,
  )
  const maxDate = dayjs().add(MAX_YEARS_AHEAD, 'year').toDate()
  const handleErrorMessage = useCallback(() => {
    return {
      dateError: isDateInPast ? 'Date is in the past' : null,
      timeError: isTimeInPast ? 'Time is in the past' : null,
    }
  }, [isTimeInPast, isDateInPast])
  const handleMinDateForTimeZone = useCallback(() => {
    // TODO: control doesn't handle minDate with timezones correctly, only subset of timezones can be used with the date in the past
    /*
    if (selectedTimeZone?.value) {
      setMinDate(new Date(formatZonedTimeNow))
    } else {
      setMinDate(new Date())
    }
    */
    setMinDate(new Date())
  }, [selectedTimeZone?.value, formatZonedTimeNow])
  useEffect(() => {
    handleMinDateForTimeZone()
    setErrorMessage(handleErrorMessage())
    prevSelectedTime.current = selectedDateTime
  }, [handleErrorMessage, handleMinDateForTimeZone])

  const currentUtcDatetime =
    selectedDate && selectedTime && selectedTimeZone?.value
      ? getTimeDateToScheduleEmail(
          new Date(selectedDate),
          selectedTime.value,
          selectedTimeZone.value,
        )
      : null

  const isUnchangedFromInitial =
    !!initialUtcDatetime &&
    !!currentUtcDatetime &&
    dayjs(currentUtcDatetime).isSame(dayjs(initialUtcDatetime), 'minute')

  const handleOnSubmit = e => {
    e.preventDefault()

    if (selectedDate && selectedTimeZone && selectedTime) {
      setScheduleData({
        date: selectedDate,
        time: selectedTime,
        timeZone: selectedTimeZone,
      })

      onClose()
    }
  }

  return (
    <DiscoModal
      onRequestClose={onClose}
      showCloseButton={showCloseButton}
      isOpen
      onClose={onClose}
      title={caption}
    >
      <Box minWidth="350px" maxWidth="360px">
        <Stack space="small">
          <Heading size={600}>{caption}</Heading>
          <Text type="paragraph">{description}</Text>
          <Stack space="xsmall">
            <RadixFlex gap="3" align="start">
              <Box flexGrow="1" minWidth="0">
                <DatePicker
                  label="Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  minDate={new Date(minDate)}
                  maxDate={maxDate}
                  dataFormat="MMMM d, yyyy"
                />
                {showInlineErrors && (
                  <div
                    className={cn({
                      [styles.errorMessageContainer]:
                        errorMessage?.timeError || errorMessage?.dateError,
                    })}
                  >
                    <InlineError
                      error={errorMessage?.dateError}
                      status={'danger'}
                    />
                  </div>
                )}
              </Box>
              <Box flexGrow="1" minWidth="0">
                <Label caption="Time" />
                <TimePicker
                  date={new Date(selectedDate || initialDate)}
                  selectedTime={selectedTime?.value || initialStartTime?.value}
                  onSelect={handleTimeChange}
                  timeIncrement={30}
                  timeZone={selectedTimeZone?.value || ''}
                  timeInAdvance={15}
                  onClick={() => setInitialStartTime(initialFormattedStartTime)}
                />
                {showInlineErrors && (
                  <div
                    className={cn({
                      [styles.errorMessageContainer]:
                        errorMessage?.timeError || errorMessage?.dateError,
                    })}
                  >
                    <InlineError
                      error={errorMessage?.timeError}
                      status={'danger'}
                    />
                  </div>
                )}
              </Box>
            </RadixFlex>
            <Flex>
              <Flex.Content>
                <Label caption="Timezone" />
                <TimeZonePicker
                  selectedTimeZone={selectedTimeZone?.value}
                  onSelect={handleTimeZoneChange}
                  selectedDate={new Date(selectedDate || initialDate)}
                />
              </Flex.Content>
            </Flex>
            {(errorMessage?.timeError || errorMessage?.dateError) &&
              errorExplanation && (
                <DsText size="1" color="red">
                  {errorExplanation}
                </DsText>
              )}
            <ButtonContainer alignRight>
              <Button caption="Cancel" onClick={onClose} />
              <Button
                disabled={
                  !selectedTimeZone ||
                  !selectedTime ||
                  !selectedDate ||
                  !!errorMessage?.timeError ||
                  !!errorMessage?.dateError ||
                  isUnchangedFromInitial
                }
                appearance="primary"
                caption={primaryButtonCaption}
                onClick={handleOnSubmit}
              />
            </ButtonContainer>
          </Stack>
        </Stack>
      </Box>
    </DiscoModal>
  )
}
